import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Funnel, X, CaretLeft, CaretRight, CaretRight as ChevronRight, Stethoscope, MapPin } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { ALL_SPECIALTIES, ALL_STATES } from "../lib/seoContent";

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" }
];

const ITEMS_PER_PAGE = 20;

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [entityType, setEntityType] = useState(searchParams.get("enumeration_type") || "");
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "");
  const [specialtyFilter, setSpecialtyFilter] = useState(searchParams.get("taxonomy_description") || "");
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Original search params (fall back to URL-provided city if no filter set)
      if (searchParams.get("first_name")) params.first_name = searchParams.get("first_name");
      if (searchParams.get("last_name")) params.last_name = searchParams.get("last_name");
      if (searchParams.get("number")) params.number = searchParams.get("number");
      if (cityFilter) params.city = cityFilter;
      else if (searchParams.get("city")) params.city = searchParams.get("city");
      
      // Filter params
      if (entityType) params.enumeration_type = entityType;
      if (stateFilter) params.state = stateFilter;
      if (specialtyFilter) params.taxonomy_description = specialtyFilter;
      
      // Pagination
      params.limit = ITEMS_PER_PAGE;
      params.skip = (currentPage - 1) * ITEMS_PER_PAGE;

      const data = await api.searchProviders(params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [searchParams, entityType, stateFilter, specialtyFilter, cityFilter, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const updateFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    
    if (entityType) {
      newParams.set("enumeration_type", entityType);
    } else {
      newParams.delete("enumeration_type");
    }
    
    if (stateFilter) {
      newParams.set("state", stateFilter);
    } else {
      newParams.delete("state");
    }
    
    if (specialtyFilter) {
      newParams.set("taxonomy_description", specialtyFilter);
    } else {
      newParams.delete("taxonomy_description");
    }

    if (cityFilter) {
      newParams.set("city", cityFilter);
    } else {
      newParams.delete("city");
    }
    
    setSearchParams(newParams);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const clearFilters = () => {
    setEntityType("");
    setStateFilter("");
    setSpecialtyFilter("");
    setCityFilter("");
    
    const newParams = new URLSearchParams();
    if (searchParams.get("first_name")) newParams.set("first_name", searchParams.get("first_name"));
    if (searchParams.get("last_name")) newParams.set("last_name", searchParams.get("last_name"));
    if (searchParams.get("number")) newParams.set("number", searchParams.get("number"));
    
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const hasActiveFilters = entityType || stateFilter || specialtyFilter;

  // Generate search title for SEO
  const searchTitle = (() => {
    const parts = [];
    if (searchParams.get("first_name") || searchParams.get("last_name")) {
      parts.push(`${searchParams.get("first_name") || ""} ${searchParams.get("last_name") || ""}`.trim());
    }
    if (searchParams.get("taxonomy_description") || specialtyFilter) {
      parts.push(searchParams.get("taxonomy_description") || specialtyFilter);
    }
    if (stateFilter) {
      const state = US_STATES.find(s => s.code === stateFilter);
      if (state) parts.push(state.name);
    }
    return parts.length > 0 ? parts.join(" in ") : "Search Results";
  })();

  return (
    <>
      <Helmet>
        <title>{searchTitle + " — NPI Lookup & Registry Search"}</title>
        <meta name="description" content={"NPI lookup — " + totalCount + " providers found. Search the NPPES national NPI registry by name, specialty, or NPI number and verify credentials instantly."} />
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">
                Search Results
              </h1>
              <p className="text-slate-500 mt-1" data-testid="result-count">
                {loading ? "Searching..." : `Showing ${results.length} providers per page`}
              </p>
            </div>
            
            {/* Mobile filter toggle */}
            <Button
              variant="outline"
              className="lg:hidden flex items-center gap-2"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              data-testid="mobile-filter-toggle"
            >
              <Funnel className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-teal rounded-full" />
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Filters Sidebar */}
            <aside className={`lg:col-span-3 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display font-semibold text-lg text-slate-900">
                    Filters
                  </h2>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-teal hover:text-teal/80 font-medium"
                      data-testid="clear-filters-button"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Entity Type Filter */}
                <div className="filter-section">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Entity Type
                  </label>
                  <Select value={entityType} onValueChange={setEntityType}>
                    <SelectTrigger data-testid="filter-entity-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="NPI-1">Individual</SelectItem>
                      <SelectItem value="NPI-2">Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* State Filter */}
                <div className="filter-section">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    State
                  </label>
                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger data-testid="filter-state">
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Specialty Filter */}
                <div className="filter-section">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Specialty
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Cardiology"
                    value={specialtyFilter}
                    onChange={(e) => setSpecialtyFilter(e.target.value)}
                    className="w-full"
                    data-testid="filter-specialty"
                  />
                </div>

                {/* City Filter */}
                <div className="filter-section">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Bend, Austin"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") updateFilters(); }}
                    className="w-full"
                    data-testid="filter-city"
                  />
                </div>

                <Button 
                  onClick={updateFilters} 
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="apply-filters-button"
                >
                  Apply Filters
                </Button>
              </div>
            </aside>

            {/* Results Grid */}
            <main className="lg:col-span-9">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                      <div className="skeleton h-4 w-20 mb-3 rounded" />
                      <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                      <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                      <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                      <div className="skeleton h-10 w-full rounded" />
                    </div>
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                    No providers found
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Try adjusting your search criteria or clearing filters
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    data-testid="new-search-button"
                  >
                    Start a new search
                  </Button>
                </div>
              ) : (
                <>
                  <div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    data-testid="search-results-grid"
                  >
                    {results.map((provider) => (
                      <ProviderCard key={provider.number} provider={provider} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {(hasMore || currentPage > 1) && (
                    <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                        data-testid="pagination-prev"
                      >
                        <CaretLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      
                      <span className="text-sm text-slate-500 font-medium px-3">
                        Page {currentPage}
                      </span>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => p + 1)}
                        disabled={!hasMore}
                        className="pagination-btn"
                        data-testid="pagination-next"
                      >
                        Next
                        <CaretRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>

          {/* Related Searches / Browse Suggestions - SEO internal links */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="browse-suggestions">
            {/* Browse by Specialty */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Stethoscope className="w-5 h-5 text-teal" />
                <h2 className="font-display font-semibold text-lg text-slate-900">
                  Browse by Specialty
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ALL_SPECIALTIES.slice(0, 8).map(s => (
                  <Link
                    key={s.slug}
                    to={`/specialty/${s.slug}`}
                    className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-700 text-sm transition-colors"
                  >
                    <span>{s.name}</span>
                    <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Browse by State */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="font-display font-semibold text-lg text-slate-900">
                  Browse by State
                </h2>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {ALL_STATES.slice(0, 20).map(st => (
                  <Link
                    key={st.code}
                    to={`/state/${st.code}`}
                    className="px-2 py-1.5 text-xs bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-600 text-center transition-colors"
                  >
                    {st.code}
                  </Link>
                ))}
                <Link
                  to="/state/CA"
                  className="col-span-2 px-2 py-1.5 text-xs bg-slate-100 rounded-lg text-slate-500 text-center"
                >
                  ...and {ALL_STATES.length - 20} more states
                </Link>
              </div>
            </div>
          </div>

          {/* SEO text block */}
          <div className="mt-8 mb-8 bg-white rounded-lg border border-slate-200 p-6" data-testid="search-seo-content">
            <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
              About NPI Lookup Provider Search
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              NPI Lookup searches the National Provider Identifier registry maintained by the Centers for Medicare
              & Medicaid Services (CMS). With over 6.2 million registered healthcare providers, you can verify
              credentials, find contact information, and look up any doctor, nurse practitioner, therapist, or
              healthcare organization in the United States. Each provider has a unique 10-digit NPI number used
              for healthcare billing and identification purposes.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResults;
